import React, { useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useI18n } from "@/context/I18nContext";

const TONE = { confirmed: "bg-emerald-100 text-emerald-800", seller_provided: "bg-amber-100 text-amber-800",
  estimated: "bg-sky-100 text-sky-800", unknown: "bg-stone-100 text-stone-600", unverified: "bg-stone-100 text-stone-600" };

export const Label = ({ k, v }) => {
  const { t } = useI18n();
  return <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold ${TONE[v] || TONE.unknown}`} data-testid={`label-${k}-${v}`}>{t(`market.field.${k}`)}: {t(`market.label.${v}`)}</span>;
};

export const ResultCard = ({ r, idx }) => {
  const { t } = useI18n();
  const { user } = useAuth() || {};
  const [busy, setBusy] = useState(false);
  const add = async () => {
    if (!user) { toast.error(t("market.signin_cart")); return; }
    setBusy(true);
    try { await api.post("/commerce/cart/add", { item_id: r.id, qty: 1 }); toast.success(t("market.added")); }
    catch (e) { toast.error(e?.response?.data?.detail || t("market.error")); } finally { setBusy(false); }
  };
  const price = r.price_inr ? `₹${r.price_inr}${r.price_unit ? ` / ${r.price_unit}` : ""}` : t("market.price_unknown");
  return (
    <div className="rounded-2xl border border-stone-200 bg-white p-3 flex gap-3 min-w-0" data-testid={`result-${r.source}-${idx}`}>
      {r.image ? <img src={r.image} alt="" className="w-16 h-16 rounded-xl object-cover shrink-0" /> : <div className="w-16 h-16 rounded-xl bg-stone-100 shrink-0" />}
      <div className="min-w-0 flex-1 space-y-1">
        <div className="text-[10px] font-black uppercase tracking-widest text-stone-400">{t(`market.source.${r.source}`)}{r.category ? ` · ${r.category}` : ""}</div>
        <div className="font-bold text-stone-900 truncate" data-testid={`result-title-${idx}`}>{r.title}</div>
        <div className="text-xs text-stone-500 truncate">{r.seller_name || "—"}{r.city ? ` · ${r.city}` : ""}{r.distance_km != null ? ` · ~${r.distance_km} km` : ""}</div>
        <div className="text-sm font-black">{price}{r.stock != null && <span className="ml-2 text-xs font-semibold text-stone-500">{t("market.stock")}: {r.stock}</span>}</div>
        <div className="flex flex-wrap gap-1">{Object.entries(r.labels || {}).map(([k, v]) => <Label key={k} k={k} v={v} />)}</div>
        <div className="flex flex-wrap gap-2 pt-1">
          <Link to={r.url} className="h-8 px-3 inline-flex items-center rounded-full border text-xs font-bold" data-testid={`result-view-${idx}`}>{t("market.view")}</Link>
          {r.can_add_to_cart && <button onClick={add} disabled={busy} className="h-8 px-3 rounded-full bg-stone-900 text-white text-xs font-bold disabled:opacity-40" data-testid={`result-cart-${idx}`}>{t("market.add_cart")}</button>}
          {r.lat != null && r.lng != null && (
            <a href={`https://www.google.com/maps/dir/?api=1&destination=${r.lat},${r.lng}`} target="_blank" rel="noreferrer" className="h-8 px-3 inline-flex items-center rounded-full border text-xs font-bold" data-testid={`result-nav-${idx}`}>{t("market.navigate")}</a>
          )}
        </div>
      </div>
    </div>
  );
};

export const StateBox = ({ state, onRetry, emptyText, testid }) => {
  const { t } = useI18n();
  if (state === "loading") return <div className="py-8 text-center text-sm text-stone-400" data-testid={`${testid}-loading`}>{t("market.loading")}</div>;
  if (state === "error") return (
    <div className="py-6 text-center text-sm space-y-2" data-testid={`${testid}-error`}>
      <div className="text-red-600">{t("market.error")}</div>
      <button onClick={onRetry} className="h-9 px-4 rounded-full border font-bold" data-testid={`${testid}-retry`}>{t("market.retry")}</button>
    </div>
  );
  if (state === "empty") return <div className="py-8 text-center text-sm text-stone-500" data-testid={`${testid}-empty`}>{emptyText}</div>;
  return null;
};
